import {
    CalendarDate,
    daysAgo,
    displayName,
    getCurrentTimezone,
    IChangeTrackOverrideable,
    isCrmAuthor,
    parseTimezonedDatetimeToUTC,
    parseUTCDatetimeToTimezone,
    Timezone,
    User,
    UserId,
} from "@sp-crm/core";
import { useDynamicCalloutPosition } from "components/community-search/search-bar/use-dynamic-callout-position";
import { Callout } from "components/ui/callout";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { useCallback, useState } from "react";
import { useProductName } from "store/selectors/branding";
import { useUser } from "store/selectors/hooks";
import Shared from "../shared";

interface BaseChangeTracked {
    dateAdded: Date;
    updatedAt?: Date;
    createdBy?: UserId | string | null | undefined;
    updatedBy?: UserId | string | null | undefined;
    dateAddedManualOverride?: Date | CalendarDate | null;
}

export interface NgBylineProps {
    entity: BaseChangeTracked;
    mode: "header" | "action-bar";
    createdAtDateModified?: (newCreatedAtDate: Date) => void;
}

export const NgByline = (props: NgBylineProps): React.ReactNode => {
    const { entity, createdAtDateModified, mode } = props;
    const createdByUser = useUser((entity.createdBy || null) as UserId);
    const updatedByUser = useUser((entity.updatedBy || null) as UserId);
    const userTimezone = getCurrentTimezone();

    const productName = useProductName();
    const [isDateInputOpen, setIsDateInputOpen] = useState(false);
    const [dynamicRef, dynamicPosition] = useDynamicCalloutPosition();

    const handleSave = useCallback(
        (value: Date) => {
            if (createdAtDateModified) {
                createdAtDateModified(value);
            }
            setIsDateInputOpen(false);
        },
        [createdAtDateModified],
    );

    const handleCancel = useCallback(() => {
        setIsDateInputOpen(false);
    }, []);

    const renderUser = useCallback(
        (userId: UserId | string | null | undefined, user?: User) => {
            if (isCrmAuthor(userId as UserId)) {
                return productName;
            }
            if (user) {
                return displayName(user);
            }
            return "Unknown";
        },
        [productName],
    );

    return (
        <div
            className={mode === "header" ? "hidden lg:block" : "inline-block"}
            ref={dynamicRef}>
            <Callout
                noPadding
                align={mode === "header" ? "right" : dynamicPosition.align}
                arrowPosition={
                    mode === "header" ? "right" : dynamicPosition.arrowPosition
                }
                renderButton={() => {
                    return (
                        <div
                            className={`text-sm cursor-pointer ${
                                mode === "header"
                                    ? "text-gray-500 underline decoration-dotted"
                                    : "rounded-full bg-gray-100 px-3 py-1 flex items-center gap-2 shadow border border-gray-200 font-medium text-gray-800 hover:bg-gray-200 transition"
                            }`}>
                            Last updated {daysAgo(entity.updatedAt || entity.dateAdded)}
                        </div>
                    );
                }}>
                {() => (
                    <div className="w-80 rounded-lg">
                        <div className="text-sm bg-white p-4 rounded-t-lg">
                            <div>
                                <span className="font-medium">Updated</span>
                                <br />
                                {renderUser(entity.updatedBy, updatedByUser)}{" "}
                                {daysAgo(entity.updatedAt || entity.dateAdded)}
                            </div>
                        </div>
                        <div
                            className={`text-sm bg-white p-4 border-t border-gray-300 ${
                                createdAtDateModified && isDateInputOpen
                                    ? ""
                                    : "rounded-b-lg"
                            }`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <span className="font-medium">Created</span>
                                    <br />
                                    {renderUser(entity.createdBy, createdByUser)}{" "}
                                    {daysAgo(entity.dateAdded)}
                                </div>
                                {createdAtDateModified && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setIsDateInputOpen(!isDateInputOpen)
                                        }
                                        className="text-gray-500 hover:text-gray-700 transition">
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                            />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        {createdAtDateModified && isDateInputOpen && (
                            <NgBylineDateInput
                                onSave={handleSave}
                                onCancel={handleCancel}
                                userTimezone={userTimezone}
                                entity={entity as IChangeTrackOverrideable}
                            />
                        )}
                    </div>
                )}
            </Callout>
        </div>
    );
};

interface NgBylineDateInputProps {
    onSave: (newCreatedAtDate: Date) => void;
    onCancel: () => void;
    userTimezone: Timezone;
    entity: IChangeTrackOverrideable;
}

const NgBylineDateInput = (props: NgBylineDateInputProps) => {
    const { onSave, onCancel, userTimezone, entity } = props;
    const [selectedDate, setSelectedDate] = useState<Date>(
        parseUTCDatetimeToTimezone(
            entity.dateAdded instanceof Date
                ? entity.dateAdded
                : new Date(entity.dateAdded),
            userTimezone,
        ),
    );

    const handleDateChange = useCallback((newDate: Date) => {
        setSelectedDate(newDate);
    }, []);

    const handleSave = useCallback(() => {
        if (onSave) {
            const date =
                typeof selectedDate === "string" ? new Date(selectedDate) : selectedDate;
            onSave(parseTimezonedDatetimeToUTC(date, userTimezone));
        }
    }, [onSave, selectedDate, userTimezone]);

    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    const handleRevert = useCallback(() => {
        onSave(null);
    }, [onSave]);

    return (
        <div className="bg-zinc-50 p-4 space-y-4 border-t border-gray-300 rounded-b-lg">
            <div>
                <Shared.DateInput
                    label={<span className="text-sm">Override the Created Date:</span>}
                    value={selectedDate}
                    onCommit={handleDateChange}
                />
            </div>

            {entity.dateAddedManualOverride && entity._dateAdded && (
                <div className="text-sm text-gray-600">
                    <div>
                        <a
                            className={defaultLinkStyle}
                            href="#"
                            onClick={e => {
                                e.preventDefault();
                                handleRevert();
                            }}>
                            Revert to original (
                            {parseUTCDatetimeToTimezone(
                                entity._dateAdded,
                                userTimezone,
                            ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "numeric",
                                day: "numeric",
                            })}
                            )
                        </a>
                    </div>
                </div>
            )}

            <div className="flex flex-row-reverse space-x-2 space-x-reverse">
                <PrimaryButton onClick={() => handleSave()}>Save</PrimaryButton>
                <SecondaryButton onClick={() => handleCancel()}>Cancel</SecondaryButton>
            </div>
        </div>
    );
};
